import React, { useState, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Map, Calendar, Music, Target, Settings, CheckCircle, ArrowRight, ArrowLeft, MapPin, Clock, Users, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
const STEPS = [{
  id: 1,
  title: 'Select Artist'
}, {
  id: 2,
  title: 'Campaign Type'
}, {
  id: 3,
  title: 'Dates & Region'
}, {
  id: 4,
  title: 'Routing Rules'
}, {
  id: 5,
  title: 'Venue Criteria'
}, {
  id: 6,
  title: 'Review'
}];
export function CampaignNewPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 6));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-6xl mx-auto space-y-6" data-id="element-2425">
      <div className="flex items-center justify-between" data-id="element-2426">
        <div data-id="element-2427">
          <h1 className="text-2xl font-bold text-zinc-100" data-id="element-2428">
            Create New Campaign
          </h1>
          <p className="text-zinc-400" data-id="element-2429">
            Plan a new tour run, one-off, or festival circuit.
          </p>
        </div>
        <Link to="/app/campaigns" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors" data-id="element-2430">
          Cancel
        </Link>
      </div>

      {/* Progress Bar */}
      <div className="flex items-center justify-between mb-8" data-id="element-2431">
        {STEPS.map((step, index) => <Fragment key={step.id} data-id="element-2432">
            <div className="flex flex-col items-center" data-id="element-2433">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-2 transition-colors ${currentStep > step.id ? 'bg-green-500 text-zinc-900' : currentStep === step.id ? 'bg-amber-500 text-zinc-900' : 'bg-zinc-800 text-zinc-500'}`} data-id="element-2434">
                {currentStep > step.id ? <CheckCircle className="w-5 h-5" data-id="element-2435" /> : step.id}
              </div>
              <span className={`text-xs font-medium ${currentStep >= step.id ? 'text-zinc-100' : 'text-zinc-500'}`} data-id="element-2436">
                {step.title}
              </span>
            </div>
            {index < STEPS.length - 1 && <div className={`flex-1 h-px mx-4 ${currentStep > step.id ? 'bg-green-500' : 'bg-zinc-800'}`} data-id="element-2437" />}
          </Fragment>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2438">
        {/* Main Form Area */}
        <div className="lg:col-span-2 bg-[#14141A] border border-zinc-800 rounded-xl p-8 min-h-[400px]" data-id="element-2439">
          {currentStep === 1 && <div className="space-y-6" data-id="element-2440">
              <h2 className="text-xl font-bold text-zinc-100" data-id="element-2441">
                Which artist is this for?
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-2442">
                {['Neon Dreams', 'The Midnight Echo', 'Sarah Williams'].map(artist => <button key={artist} className="p-4 bg-zinc-900 border border-zinc-800 hover:border-amber-500 rounded-xl text-left transition-colors focus:border-amber-500 focus:ring-1 focus:ring-amber-500" data-id="element-2443">
                      <Music className="w-6 h-6 text-zinc-500 mb-3" data-id="element-2444" />
                      <h3 className="font-medium text-zinc-100" data-id="element-2445">{artist}</h3>
                    </button>)}
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-2446">
              <h2 className="text-xl font-bold text-zinc-100" data-id="element-2447">
                What type of campaign?
              </h2>
              <div className="space-y-4" data-id="element-2448">
                {[{
              title: 'Tour Run',
              desc: 'A connected series of dates across a region.'
            }, {
              title: 'One-Off / Weekend',
              desc: 'A single show or short weekend trip.'
            }, {
              title: 'Festival Circuit',
              desc: 'Routing built around anchor festival dates.'
            }].map(type => <button key={type.title} className="w-full p-4 bg-zinc-900 border border-zinc-800 hover:border-amber-500 rounded-xl text-left transition-colors flex items-start space-x-4" data-id="element-2449">
                    <div className="w-5 h-5 rounded-full border border-zinc-600 mt-0.5" data-id="element-2450" />
                    <div data-id="element-2451">
                      <h3 className="font-medium text-zinc-100" data-id="element-2452">
                        {type.title}
                      </h3>
                      <p className="text-sm text-zinc-400 mt-1" data-id="element-2453">{type.desc}</p>
                    </div>
                  </button>)}
              </div>
            </div>}

          {currentStep === 3 && <div className="space-y-6" data-id="element-2454">
              <h2 className="text-xl font-bold text-zinc-100" data-id="element-2455">
                Dates & Region
              </h2>
              <div className="space-y-6" data-id="element-2456">
                <div className="grid grid-cols-2 gap-4" data-id="element-2457">
                  <div data-id="element-2458">
                    <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2459">
                      Start Date
                    </label>
                    <div className="relative" data-id="element-2460">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" data-id="element-2461" />
                      <input type="date" className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500" data-id="element-2462" />
                    </div>
                  </div>
                  <div data-id="element-2463">
                    <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2464">
                      End Date
                    </label>
                    <div className="relative" data-id="element-2465">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" data-id="element-2466" />
                      <input type="date" className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500" data-id="element-2467" />
                    </div>
                  </div>
                </div>
                <div data-id="element-2468">
                  <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2469">
                    Target Regions
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-2470">
                    {['Northeast', 'Midwest', 'South', 'West Coast', 'Pacific NW', 'Canada'].map(region => <label key={region} className="flex items-center space-x-3 p-3 bg-zinc-900 border border-zinc-800 rounded-lg cursor-pointer hover:border-zinc-700 transition-colors" data-id="element-2471">
                        <input type="checkbox" className="rounded border-zinc-700 text-amber-500 focus:ring-amber-500 bg-zinc-800" data-id="element-2472" />
                        <span className="text-sm text-zinc-300" data-id="element-2473">{region}</span>
                      </label>)}
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 4 && <div className="space-y-6" data-id="element-2474">
              <h2 className="text-xl font-bold text-zinc-100" data-id="element-2475">Routing Rules</h2>
              <div className="space-y-6" data-id="element-2476">
                <div data-id="element-2477">
                  <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2478">
                    Max Drive Time (Hours)
                  </label>
                  <div className="flex items-center space-x-4" data-id="element-2479">
                    <input type="range" min="1" max="12" defaultValue="5" className="flex-1 accent-amber-500" data-id="element-2480" />
                    <span className="w-12 text-center text-zinc-100 font-medium bg-zinc-900 py-1 rounded border border-zinc-800" data-id="element-2481">
                      5h
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-2" data-id="element-2482">
                    Maximum driving time between consecutive shows.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-2483">
                  <div data-id="element-2484">
                    <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2485">
                      Max Consecutive Shows
                    </label>
                    <select className="w-full px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500" data-id="element-2486">
                      <option data-id="element-2487">3 days</option>
                      <option data-id="element-2488">4 days</option>
                      <option data-id="element-2489">5 days</option>
                      <option data-id="element-2490">No limit</option>
                    </select>
                  </div>
                  <div data-id="element-2491">
                    <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2492">
                      Preferred Off Days
                    </label>
                    <select className="w-full px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500" data-id="element-2493">
                      <option data-id="element-2494">Mondays</option>
                      <option data-id="element-2495">Sundays & Mondays</option>
                      <option data-id="element-2496">Tuesdays</option>
                      <option data-id="element-2497">Flexible</option>
                    </select>
                  </div>
                </div>
                <div data-id="element-2498">
                  <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2499">
                    Anchor Dates (Optional)
                  </label>
                  <div className="p-4 bg-zinc-900/50 border border-zinc-800 border-dashed rounded-lg text-center" data-id="element-2500">
                    <p className="text-sm text-zinc-400 mb-2" data-id="element-2501">
                      Add festivals or already confirmed shows to route around.
                    </p>
                    <button className="text-amber-500 hover:text-amber-400 text-sm font-medium flex items-center justify-center w-full" data-id="element-2502">
                      <Plus className="w-4 h-4 mr-1" data-id="element-2503" /> Add Anchor Date
                    </button>
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 5 && <div className="space-y-6" data-id="element-2504">
              <h2 className="text-xl font-bold text-zinc-100" data-id="element-2505">
                Venue Criteria
              </h2>
              <div className="space-y-6" data-id="element-2506">
                <div className="grid grid-cols-2 gap-4" data-id="element-2507">
                  <div data-id="element-2508">
                    <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2509">
                      Min Capacity
                    </label>
                    <div className="relative" data-id="element-2510">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" data-id="element-2511" />
                      <input type="number" placeholder="200" className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500" data-id="element-2512" />
                    </div>
                  </div>
                  <div data-id="element-2513">
                    <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2514">
                      Max Capacity
                    </label>
                    <div className="relative" data-id="element-2515">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" data-id="element-2516" />
                      <input type="number" placeholder="500" className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500" data-id="element-2517" />
                    </div>
                  </div>
                </div>
                <div data-id="element-2518">
                  <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-2519">
                    Genre Fit Requirement
                  </label>
                  <select className="w-full px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500" data-id="element-2520">
                    <option data-id="element-2521">Strict (Must match primary genres)</option>
                    <option data-id="element-2522">Flexible (Includes secondary genres)</option>
                    <option data-id="element-2523">Broad (Any venue in capacity range)</option>
                  </select>
                </div>
                <div data-id="element-2524">
                  <label className="flex items-center space-x-3 p-4 bg-zinc-900 border border-zinc-800 rounded-lg cursor-pointer hover:border-zinc-700 transition-colors" data-id="element-2525">
                    <input type="checkbox" defaultChecked className="rounded border-zinc-700 text-amber-500 focus:ring-amber-500 bg-zinc-800 w-5 h-5" data-id="element-2526" />
                    <div data-id="element-2527">
                      <span className="block text-sm font-medium text-zinc-100" data-id="element-2528">
                        Require Verified Contacts
                      </span>
                      <span className="block text-xs text-zinc-500 mt-1" data-id="element-2529">
                        Only target venues with high-confidence booking emails.
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            </div>}

          {currentStep === 6 && <div className="space-y-6" data-id="element-2530">
              <h2 className="text-xl font-bold text-zinc-100" data-id="element-2531">
                Review & Generate
              </h2>
              <p className="text-zinc-400" data-id="element-2532">
                Review your campaign settings before generating the initial
                route and targets.
              </p>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 flex items-center justify-center h-48" data-id="element-2533">
                <p className="text-zinc-500" data-id="element-2534">
                  Visual Route Preview Placeholder
                </p>
              </div>
            </div>}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-800" data-id="element-2535">
            <button onClick={prevStep} disabled={currentStep === 1} className="flex items-center space-x-2 px-4 py-2 text-zinc-400 hover:text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-2536">
              <ArrowLeft className="w-4 h-4" data-id="element-2537" />
              <span data-id="element-2538">Back</span>
            </button>
            {currentStep < 6 ? <button onClick={nextStep} className="flex items-center space-x-2 px-6 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-medium rounded-lg transition-colors" data-id="element-2539">
                <span data-id="element-2540">Next</span>
                <ArrowRight className="w-4 h-4" data-id="element-2541" />
              </button> : <button className="flex items-center space-x-2 px-6 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-medium rounded-lg transition-colors" data-id="element-2542">
                <Target className="w-4 h-4" data-id="element-2543" />
                <span data-id="element-2544">Generate Route & Targets</span>
              </button>}
          </div>
        </div>

        {/* Side Summary Panel */}
        <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6 h-fit sticky top-6" data-id="element-2545">
          <h3 className="font-medium text-zinc-100 mb-4 pb-4 border-b border-zinc-800" data-id="element-2546">
            Campaign Summary
          </h3>
          <div className="space-y-4 text-sm" data-id="element-2547">
            <div data-id="element-2548">
              <p className="text-zinc-500 mb-1" data-id="element-2549">Artist</p>
              <p className="text-zinc-300 font-medium" data-id="element-2550">
                {currentStep > 1 ? 'Neon Dreams' : '--'}
              </p>
            </div>
            <div data-id="element-2551">
              <p className="text-zinc-500 mb-1" data-id="element-2552">Type</p>
              <p className="text-zinc-300 font-medium" data-id="element-2553">
                {currentStep > 2 ? 'Tour Run' : '--'}
              </p>
            </div>
            <div data-id="element-2554">
              <p className="text-zinc-500 mb-1" data-id="element-2555">Dates</p>
              <p className="text-zinc-300 font-medium" data-id="element-2556">
                {currentStep > 3 ? 'Fall 2026' : '--'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}